import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { ShieldAlert, Lock, Eye, EyeOff, CheckCircle2, ArrowRight } from "lucide-react";
import { User } from "../../types";
import { apiService } from "../../utils/apiService";

interface ChangePasswordModalProps {
  currentUser: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedUser: User) => void;
  forced?: boolean;
}

function passwordPolicyError(password: string): string | null {
  if (password.length < 12) return "Password must contain at least 12 characters.";
  if (!/[a-z]/.test(password)) return "Password must contain a lowercase letter.";
  if (!/[A-Z]/.test(password)) return "Password must contain an uppercase letter.";
  if (!/\d/.test(password)) return "Password must contain a number.";
  if (!/[^A-Za-z0-9]/.test(password)) return "Password must contain a special character.";
  return null;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  currentUser,
  isOpen,
  onClose,
  onSuccess,
  forced,
}) => {
  const isForcedPrompt = forced ?? Boolean(currentUser?.mustChangePassword);
  const [currentPassword, setCurrentPassword] = useState(isForcedPrompt ? "admin123" : "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentPassword(isForcedPrompt ? "admin123" : "");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setSuccessMessage(null);
  }, [isOpen, isForcedPrompt]);

  if (!currentUser) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!currentPassword) {
      setError("Please enter your current password.");
      return;
    }

    const policyErr = passwordPolicyError(newPassword);
    if (policyErr) {
      setError(policyErr);
      return;
    }

    if (newPassword === currentPassword || newPassword.toLowerCase() === "admin123") {
      setError("Please choose a password different from your current/default password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.changePassword(newPassword, currentPassword);

      if (response && response.error) {
        throw new Error(response.error);
      }

      setSuccessMessage("Password updated successfully!");

      const updatedUser: User = {
        ...currentUser,
        mustChangePassword: false,
      };

      setTimeout(() => {
        onSuccess(updatedUser);
        onClose();
      }, 700);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-background border-border text-foreground shadow-sm">
        <DialogHeader className="text-left space-y-2">
          {isForcedPrompt ? (
            <>
              <div className="flex items-center space-x-2 text-amber-500">
                <ShieldAlert className="h-6 w-6" />
                <DialogTitle className="text-lg font-semibold tracking-tight text-foreground">
                  Security Action Required
                </DialogTitle>
              </div>
              <DialogDescription className="text-sm text-muted-foreground">
                Your account <span className="font-semibold text-foreground">{currentUser.email}</span> is using the default seeded password (<code className="text-xs bg-muted px-1.5 py-0.5 rounded">admin123</code>). Please choose a strong password (12+ chars, upper/lowercase, number, and symbol).
              </DialogDescription>
            </>
          ) : (
            <>
              <div className="flex items-center space-x-2 text-foreground">
                <Lock className="h-5 w-5" />
                <DialogTitle className="text-lg font-semibold tracking-tight text-foreground">
                  Change Password
                </DialogTitle>
              </div>
              <DialogDescription className="text-sm text-muted-foreground">
                Update the password for <span className="font-semibold text-foreground">{currentUser.email}</span>. Use at least 12 characters with uppercase, lowercase, a number, and a symbol.
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="py-2 text-sm border-destructive/40 bg-destructive/10 text-destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {successMessage && (
          <Alert className="py-2 text-sm border-border bg-muted text-foreground">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{successMessage}</AlertDescription>
            </div>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-2">
            <Label htmlFor="current-pw" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Current Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="current-pw"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="pl-9 pr-10 bg-muted/40 font-mono text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-pw" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              New Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="new-pw"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="12+ chars, upper, lower, digit, symbol"
                className="pl-9 pr-10 bg-muted/40 font-mono text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                aria-label={showNewPassword ? "Hide new password" : "Show new password"}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-pw" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Confirm New Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirm-pw"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                className="pl-9 pr-10 bg-muted/40 font-mono text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border mt-5">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {isForcedPrompt ? "Remind me later" : "Cancel"}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || Boolean(successMessage)}
              className="px-5 bg-primary text-primary-foreground hover:bg-secondary font-medium"
            >
              {isLoading ? "Updating..." : (
                <span className="flex items-center space-x-1.5">
                  <span>Save Password</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
